@if (loading() && !summary()) {
  <section class="briefing-card briefing-card--state" role="status">
    <app-cog-loading-spinner [size]="56" />
    <p>Carregando o briefing do deal...</p>
  </section>
} @else if (loadError(); as error) {
  <section class="briefing-card briefing-card--state" role="alert">
    <strong class="briefing-error">{{ error }}</strong>
    <app-button
      [label]="'Tentar de novo'"
      [theme]="'outline-grey'"
      [icon]="'refresh'"
      (sendButtonClick)="load()" />
  </section>
} @else if (summary()) {
  <!-- `briefing-shell` é o container das colunas (container query); o chat fica fora dele, sem herdar contenção. -->
  <div #shell class="briefing-shell" role="tabpanel">
    <!-- Modo foco (Q11): com o Cog IA aberto, o painel Nova versão recolhe (reabre pelo ícone na barra do leitor) e o
         leitor ocupa a largura toda ao lado do chat. Colunas lado a lado: as duas vão até o fim da janela
         (`fillHeight`), com o pé do painel alinhado ao pé do leitor. -->
    <div
      class="briefing"
      [class.briefing--focus]="chatOpen()"
      [class.briefing--fill]="sideBySide() && !chatOpen()">
      <!-- ═══════════════ coluna esquerda: gerar ═══════════════ -->
      <section
        class="briefing-card briefing__generate"
        [class.briefing__generate--collapsed]="chatOpen() && !generatePanelOpen()"
        aria-labelledby="briefing-generate-title">
        <!-- B1 da crítica da opção B: o painel cresce com o conteúdo (nada de roteiro cortado sem sinal) e acompanha a
             rolagem da página; só o leitor fica preso à altura da janela. -->
        <div class="briefing__generate-body">
          <h3 id="briefing-generate-title" class="briefing__heading">
            {{ versions().length ? 'Nova versão' : 'Gerar briefing' }}
          </h3>

          <!-- Prontidão compacta (protótipo `_s-pronto`): ícone redondo, título e a mensagem do backend. -->
          @if (readiness(); as r) {
            <div
              class="readiness readiness--{{ tone() }}"
              [attr.role]="tone() === 'blocked' ? 'alert' : null">
              <span class="readiness__icon" aria-hidden="true">
                <i
                  class="pi"
                  [class.pi-check]="tone() === 'ready'"
                  [class.pi-file-edit]="tone() === 'reading'"
                  [class.pi-ban]="tone() === 'blocked'"></i>
              </span>
              <div class="readiness__body">
                <p class="readiness__title">{{ readinessTitles[tone()] }}</p>
                <!-- G1 da crítica: o título fica só aqui (a mensagem do backend vem sem ele) e "demora mais" aparece
                   uma vez: o aviso curto só entra se a mensagem do backend não disser. -->
                <p class="readiness__message">
                  {{ readinessMessage() }}
                  @if (tone() === 'reading' && !readinessMentionsSlowness()) {
                    O agente vai ler {{ documentCount(documentsToRead().length) }} direto; a geração
                    demora mais.
                  }
                </p>
                <!-- N1/N4: bloqueado leva ao lugar de agir (aba Documentos do deal, com o Data Room da empresa). -->
                @if (tone() === 'blocked') {
                  <a
                    class="readiness__link"
                    [routerLink]="[]"
                    [queryParams]="{ tab: 'documents' }"
                    queryParamsHandling="merge">
                    Abrir o Data Room
                    <i class="pi pi-arrow-right" aria-hidden="true"></i>
                  </a>
                }
              </div>
            </div>
          }

          <!-- Investigação da empresa (card 3911): estado, validade e Refazer. Só com a seção no catálogo e com `VIEW`. -->
          @if (showInvestigationBox()) {
            <app-briefing-investigation-status
              [investigation]="investigation.investigation()"
              [progress]="investigation.progress()"
              [catalog]="catalog()"
              [selectedInRoadmap]="investigationSelected()"
              [rerunning]="investigation.rerunning()"
              [disabled]="tone() === 'blocked'"
              [canRerun]="can.canRerunInvestigation()"
              (rerun)="rerunInvestigation()" />
          }

          <!-- Sem `RUN`, a Investigação e as subseções vêm desligadas, com o motivo (`lockedReasons`). -->
          <app-briefing-section-picker
            [catalog]="selectableCatalog()"
            [lockedReasons]="lockedReasons()"
            [selected]="selected()"
            [format]="format()"
            [disabled]="tone() === 'blocked' || !!activeVersion()"
            (toggled)="onToggle($event)"
            (formatChosen)="onFormat($event)" />
        </div>

        <!-- Rodapé (protótipo `_s-rodape`): contagem, estimativa de páginas, barra de 3 segmentos e Gerar. -->
        <div class="briefing__generate-footer">
          <div class="estimate" [class.estimate--over]="pageEstimate().overTarget">
            <div class="estimate__line">
              <span class="estimate__count">
                <strong>{{ selectedCount() }}</strong> seções ·
                {{
                  pageEstimate().pageCount === 1
                    ? 'cerca de 1 página'
                    : 'cerca de ' + pageEstimate().pageCount + ' páginas'
                }}
                @if (pageEstimate().annexPageCount) {
                  + anexo de {{ pageEstimate().annexPageCount }}
                  {{ pageEstimate().annexPageCount === 1 ? 'página' : 'páginas' }}
                }
              </span>
              <span class="estimate__target">alvo: até {{ targetPages }} páginas</span>
            </div>
            <div class="estimate__bar" aria-hidden="true">
              @for (segment of pageSegments; track segment) {
                <span
                  class="estimate__segment"
                  [class.estimate__segment--filled]="pageEstimate().pageCount >= segment"></span>
              }
            </div>
            @if (pageEstimate().overTarget) {
              <p class="estimate__warning" role="status">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                Pode passar de {{ targetPages }} páginas; o agente enxuga o texto.
              </p>
            }
          </div>
          <app-button
            class="briefing__generate-button"
            [label]="activeVersion() ? 'Gerando...' : 'Gerar briefing'"
            [icon]="'sparkles'"
            [theme]="'default'"
            [isLoading]="requesting()"
            [loadingText]="'Pedindo...'"
            [disabled]="!canGenerate()"
            (sendButtonClick)="generate()" />
          @if (!can.canGenerate()) {
            <p class="briefing__hint">{{ noGenerateHint }}</p>
          } @else if (tone() === 'blocked') {
            <p class="briefing__hint">
              Gerar fica disponível quando houver exercício lido ou documento contábil no Data Room.
            </p>
          } @else if (activeVersion()) {
            <p class="briefing__hint">Uma geração por vez neste deal.</p>
          }
        </div>
      </section>

      <!-- ═══════════════ coluna direita: resultado e versões ═══════════════ -->
      <div class="briefing__result">
        <!-- A geração em andamento mora no cabeçalho da versão (selo com a fase no menu, barra fina de progresso e o
             pedido na dica do selo); o cartão de fases separado saiu, porque espremia a prévia. -->
        @if (viewedVersion(); as v) {
          @let generating = activeVersion();
          @let generationHint =
            generating
              ? 'Pedido por ' +
                (generating.requestedByName || 'Cog IA') +
                ' em ' +
                (generating.createdAt | vaultDateTime) +
                '. Pode sair da aba: o rascunho se atualiza sozinho e a versão fica aqui quando terminar.' +
                (currentVersion()
                  ? ' A versão ' + currentVersion()!.versionNumber + ' segue como atual até lá.'
                  : '')
              : null;
          <!-- Q11, opção B do protótipo (`_vb-visor`): visualizador com o menu Versões no título; o cartão de versões
               de baixo saiu. "O que mudou" fica ABAIXO do documento (decisão do Victor). -->
          <section
            #viewer
            class="briefing-card briefing-card--flush viewer"
            [class.viewer--fill]="v.status !== 'Failed'"
            [style.height.px]="v.status !== 'Failed' ? fillHeight() : null"
            [style.--briefing-preview-min]="fillHeight() - previewChrome + 'px'">
            <div class="viewer__bar">
              <app-briefing-version-menu
                [versions]="versions()"
                [catalog]="catalog()"
                [viewed]="v"
                [settingCurrentId]="settingCurrentId()"
                [canSetCurrent]="can.canSetCurrent()"
                [phaseHint]="generating?.id === v.id ? generationHint : null"
                (versionSelected)="onViewVersion($event)"
                (makeCurrent)="setCurrent($event)" />
              <!-- Vendo outra versão durante uma geração: atalho para o rascunho ao vivo. -->
              @if (activeVersion(); as active) {
                @if (active.id !== v.id) {
                  <button
                    type="button"
                    class="viewer__generating"
                    [pTooltip]="generationHint ?? ''"
                    tooltipPosition="bottom"
                    (click)="onViewVersion(active.id)">
                    <app-cog-loading-spinner [size]="14" />
                    Gerando a v{{ active.versionNumber }}: {{ phases[activePhaseIndex()] }}
                  </button>
                }
              }
              @if (!chatOpen() && v.status !== 'Failed') {
                <span class="viewer__meta">
                  {{ v.completedAt || v.createdAt | vaultDateTime }} ·
                  {{ v.requestedByName || 'Cog IA' }} · {{ v.selectedSections.length }} seções
                </span>
              }
              <!-- Custo de IA da geração: "$" discreto ao lado da data e do autor, com popover no mouse e no foco
                   (o mesmo padrão do "$" do chat). Só com o campo (o backend manda só para ADMIN) E o papel ADMIN. -->
              @if (generationCost(v); as cost) {
                <ng-container *appHasRole="'ADMIN'">
                  <span class="viewer__cost">
                    <button
                      type="button"
                      class="viewer__cost-trigger"
                      aria-label="Custo da geração"
                      aria-describedby="briefing-generation-cost">
                      <i class="pi pi-dollar" aria-hidden="true"></i>
                    </button>
                    <span id="briefing-generation-cost" class="viewer__cost-popover" role="tooltip">
                      <span class="viewer__cost-head">
                        <span>Custo da geração</span>
                        <strong>{{ cost.total }}</strong>
                      </span>
                      @for (row of cost.rows; track row.label) {
                        <span class="viewer__cost-row">
                          <span class="viewer__cost-stage">
                            {{ row.label }}
                            @if (row.model || row.tokens) {
                              <small
                                >{{ row.model }}{{ row.model && row.tokens ? ' · ' : ''
                                }}{{ row.tokens }}</small
                              >
                            }
                          </span>
                          <span class="viewer__cost-value">{{ row.cost }}</span>
                        </span>
                      }
                      @if (cost.totalBrl) {
                        <span class="viewer__cost-foot"
                          >Em reais, pela PTAX: {{ cost.totalBrl }}</span
                        >
                      }
                    </span>
                  </span>
                </ng-container>
              }
              <span class="viewer__spacer"></span>
              @if (chatOpen()) {
                <app-button
                  class="viewer__new-version"
                  [label]="''"
                  [icon]="'plus'"
                  [theme]="'white'"
                  [size]="'small'"
                  [noMargin]="true"
                  [selected]="generatePanelOpen()"
                  [ariaLabel]="generatePanelOpen() ? 'Recolher Nova versão' : 'Abrir Nova versão'"
                  [pTooltip]="generatePanelOpen() ? 'Recolher Nova versão' : 'Nova versão'"
                  tooltipPosition="bottom"
                  (sendButtonClick)="toggleGeneratePanel()" />
              }
              <!-- Botão desabilitado não recebe o hover: a dica fica no invólucro (como no seletor da jornada). -->
              <span class="viewer__icons">
                <span
                  class="viewer__pdf-action"
                  [pTooltip]="v.status === 'Ready' ? 'Baixar PDF' : pdfNotReadyHint"
                  tooltipPosition="bottom">
                  <app-button
                    [class.viewer__pdf-button--off]="v.status !== 'Ready'"
                    [label]="''"
                    [icon]="'download'"
                    [theme]="'white'"
                    [size]="'small'"
                    [noMargin]="true"
                    [ariaLabel]="
                      v.status === 'Ready' ? 'Baixar PDF' : 'Baixar PDF. ' + pdfNotReadyHint
                    "
                    [isLoading]="pdf.action() === 'download'"
                    [loadingText]="''"
                    [disabled]="v.status !== 'Ready' || pdf.action() !== null"
                    (sendButtonClick)="downloadPdf(v)" />
                </span>
                <span
                  class="viewer__pdf-action"
                  [pTooltip]="v.status === 'Ready' ? 'Abrir PDF em nova aba' : pdfNotReadyHint"
                  tooltipPosition="bottom">
                  <app-button
                    [class.viewer__pdf-button--off]="v.status !== 'Ready'"
                    [label]="''"
                    [icon]="'external-link'"
                    [theme]="'white'"
                    [size]="'small'"
                    [noMargin]="true"
                    [ariaLabel]="
                      v.status === 'Ready'
                        ? 'Abrir PDF em nova aba'
                        : 'Abrir PDF em nova aba. ' + pdfNotReadyHint
                    "
                    [isLoading]="pdf.action() === 'open'"
                    [loadingText]="''"
                    [disabled]="v.status !== 'Ready' || pdf.action() !== null"
                    (sendButtonClick)="openPdf(v)" />
                </span>
              </span>
              @if (chatOpen()) {
                <app-button
                  [label]="'Sair do foco'"
                  [theme]="'white'"
                  [size]="'small'"
                  [noMargin]="true"
                  (sendButtonClick)="closeChat()" />
              } @else {
                <app-button
                  [label]="'Cog IA'"
                  [icon]="'sparkles'"
                  [theme]="'default'"
                  [size]="'small'"
                  [noMargin]="true"
                  [ariaLabel]="
                    can.canAdjust()
                      ? 'Abrir o Cog IA para perguntar ou ajustar o briefing'
                      : 'Abrir o Cog IA para perguntar sobre o briefing'
                  "
                  (sendButtonClick)="openChat()" />
              }
            </div>

            <!-- Barra fina de progresso da geração, colada por baixo da barra da versão (fase atual de 4). -->
            @if (activeVersion(); as active) {
              <div
                class="viewer__progress"
                role="progressbar"
                aria-label="Andamento da geração"
                [attr.aria-valuemin]="1"
                [attr.aria-valuemax]="phases.length"
                [attr.aria-valuenow]="activePhaseIndex() + 1"
                [attr.aria-valuetext]="
                  'Gerando a versão ' + active.versionNumber + ': ' + phases[activePhaseIndex()]
                ">
                <span
                  class="viewer__progress-fill"
                  [style.width.%]="((activePhaseIndex() + 1) / phases.length) * 100"></span>
              </div>
            }

            <!-- Revisão final do Cog (Q13): linha discreta; com ajustes, abre a lista do que ele corrigiu. -->
            @if (finalReviewText(v); as reviewText) {
              @let reviewItems = finalReviewItems(v);
              <div class="viewer__review">
                @if (reviewItems.length) {
                  <button
                    type="button"
                    class="viewer__review-toggle"
                    [attr.aria-expanded]="finalReviewOpenId() === v.id"
                    aria-controls="briefing-final-review"
                    (click)="toggleFinalReview(v)">
                    <i class="pi pi-verified" aria-hidden="true"></i>
                    {{ reviewText }}
                    <i
                      class="pi pi-chevron-down viewer__review-chevron"
                      [class.viewer__review-chevron--open]="finalReviewOpenId() === v.id"
                      aria-hidden="true"></i>
                  </button>
                } @else {
                  <span class="viewer__review-toggle viewer__review-toggle--static">
                    <i class="pi pi-verified" aria-hidden="true"></i>
                    {{ reviewText }}
                  </span>
                }
                @if (finalReviewOpenId() === v.id && reviewItems.length) {
                  <!-- Texto do agente: só interpolação. -->
                  <ul id="briefing-final-review" class="viewer__review-list">
                    @for (item of reviewItems; track $index) {
                      <li>
                        <strong>{{ item.place }}:</strong> {{ item.correction }}
                      </li>
                    }
                  </ul>
                }
              </div>
            }

            <!-- Investigação nesta versão (card 3911): rodando, indo para o texto ou não concluída. -->
            @if (investigationNotice(v); as notice) {
              <div class="viewer__notice viewer__notice--investigation" role="status">
                <i
                  class="pi"
                  [class.pi-search]="v.investigationState !== 'not_completed'"
                  [class.pi-exclamation-triangle]="v.investigationState === 'not_completed'"
                  aria-hidden="true"></i>
                <span class="viewer__notice-text">{{ notice }}</span>
                @if (v.investigationState === 'not_completed' && v.status === 'Ready') {
                  <!-- Sem `RERUN`: desabilitado, com o motivo na dica do invólucro (botão desabilitado não tem hover). -->
                  <span
                    class="viewer__pdf-action"
                    [pTooltip]="can.canRerunInvestigation() ? '' : noRerunHint"
                    tooltipPosition="bottom">
                    <app-button
                      [label]="'Completar investigação'"
                      [icon]="'search-plus'"
                      [theme]="'white'"
                      [size]="'small'"
                      [noMargin]="true"
                      [isLoading]="investigation.completingVersionId() === v.id"
                      [disabled]="
                        !can.canRerunInvestigation() ||
                        investigation.completingVersionId() !== null ||
                        !!activeVersion()
                      "
                      [ariaLabel]="
                        can.canRerunInvestigation()
                          ? 'Completar investigação'
                          : 'Completar investigação. ' + noRerunHint
                      "
                      [title]="
                        can.canRerunInvestigation()
                          ? 'Gera uma versão nova que reescreve só Red flags e Recomendação com os riscos da investigação'
                          : ''
                      "
                      (sendButtonClick)="completeInvestigation(v)" />
                  </span>
                }
              </div>
            }

            <!-- Versão antiga aberta pelo menu: aviso discreto; trocar a atual é só pelo botão. -->
            @if (olderVersionNotice(); as notice) {
              <div class="viewer__notice" role="status">
                <i class="pi pi-info-circle" aria-hidden="true"></i>
                <span class="viewer__notice-text">
                  Você está vendo a v{{ notice.viewedNumber }}; a atual é a v{{
                    notice.currentNumber
                  }}.
                </span>
                <button type="button" class="viewer__notice-link" (click)="viewCurrent()">
                  Ver a atual
                </button>
                @if (v.status === 'Ready' && can.canSetCurrent()) {
                  <app-button
                    [label]="'Tornar atual'"
                    [icon]="'check'"
                    [theme]="'white'"
                    [size]="'small'"
                    [noMargin]="true"
                    [isLoading]="settingCurrentId() === v.id"
                    [disabled]="settingCurrentId() !== null"
                    (sendButtonClick)="setCurrent(v.id)" />
                }
              </div>
            }

            @if (v.status === 'Failed') {
              <div class="failed" role="alert">
                <span class="failed__icon" aria-hidden="true"
                  ><i class="pi pi-exclamation-triangle"></i
                ></span>
                <div class="failed__body">
                  <h3 class="briefing__title">A versão {{ v.versionNumber }} não foi gerada</h3>
                  <p class="briefing__text">
                    {{
                      v.failureMessage
                        ? failureReason(v.failureMessage)
                        : 'O agente não conseguiu terminar o briefing. Tente de novo; se repetir, use Ajustar seções e desligue uma seção opcional.'
                    }}
                  </p>
                  @if (currentVersion(); as current) {
                    <p class="briefing__muted">
                      Nada se perdeu: a versão {{ current.versionNumber }} continua como atual.
                    </p>
                  }
                  <div class="briefing__actions">
                    <app-button
                      [label]="'Tentar de novo'"
                      [icon]="'refresh'"
                      [theme]="'default'"
                      [noMargin]="true"
                      [isLoading]="requesting()"
                      [disabled]="!canGenerate()"
                      (sendButtonClick)="retry(v)" />
                    @if (currentVersion(); as current) {
                      <app-button
                        [label]="'Ver a versão ' + current.versionNumber"
                        [theme]="'white'"
                        [noMargin]="true"
                        (sendButtonClick)="viewCurrent()" />
                    }
                  </div>
                </div>
              </div>
            } @else {
              <app-briefing-preview-frame
                #preview
                class="viewer__preview"
                [view]="previewView()"
                [versionId]="v.id"
                [generating]="v.status === 'Queued' || v.status === 'Generating'"
                [versionNumber]="v.versionNumber"
                (pageCountChange)="previewPages.set($event)"
                (retry)="retryPreview(v)" />

              <!-- Pé do leitor (Q11): "O que mudou" à esquerda (abre para cima, sobre a prévia) e o aviso de
                   fidelidade à direita. Uma barra só, dentro do cartão; nunca um cartão novo embaixo. -->
              <div class="viewer__foot">
                @if (revisionSummary(); as revision) {
                  <app-briefing-revision-summary class="viewer__changes" [summary]="revision" />
                }
                <!-- Zoom da prévia (Q11) no pé, à direita: fora da prévia, nunca cobre o documento. Só aparece quando o
                     documento sabe trocar a escala (avisa `scaled`). -->
                @if (preview.shownScale() !== null) {
                  <div class="viewer__zoom" role="group" aria-label="Zoom da prévia">
                    <button
                      type="button"
                      class="viewer__zoom-button"
                      aria-label="Diminuir o zoom"
                      pTooltip="Diminuir"
                      tooltipPosition="top"
                      [disabled]="!preview.canZoomOut()"
                      (click)="preview.stepZoom(-1)">
                      <i class="pi pi-minus" aria-hidden="true"></i>
                    </button>
                    <span class="viewer__zoom-value" aria-live="polite">{{
                      preview.zoomPercent()
                    }}</span>
                    <button
                      type="button"
                      class="viewer__zoom-button"
                      aria-label="Aumentar o zoom"
                      pTooltip="Aumentar"
                      tooltipPosition="top"
                      [disabled]="!preview.canZoomIn()"
                      (click)="preview.stepZoom(1)">
                      <i class="pi pi-plus" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      class="viewer__zoom-button"
                      [class.viewer__zoom-button--active]="preview.zoom() === 'fit'"
                      [attr.aria-pressed]="preview.zoom() === 'fit'"
                      aria-label="Ajustar à largura"
                      pTooltip="Ajustar à largura"
                      tooltipPosition="top"
                      (click)="preview.fitWidth()">
                      <i class="pi pi-arrows-h" aria-hidden="true"></i>
                    </button>
                  </div>
                }
                <p class="viewer__fidelity">
                  <span class="viewer__fidelity-long"
                    >Prévia na tela; o documento oficial é o PDF</span
                  >
                  <span class="viewer__fidelity-short">Prévia · o oficial é o PDF</span>
                  @if (previewPages(); as pages) {
                    · {{ pages === 1 ? '1 página' : pages + ' páginas' }}
                  }
                </p>
              </div>
            }
          </section>
        } @else {
          <section class="briefing-card briefing-card--empty">
            <span class="empty__icon" aria-hidden="true"><i class="pi pi-file"></i></span>
            <h3 class="briefing__title">Este deal ainda não tem briefing</h3>
            @if (tone() === 'blocked') {
              <!-- N1: no bloqueado, a instrução é subir os documentos, não escolher seções (o Gerar está travado). -->
              <p class="briefing__muted">
                Para gerar, suba o balanço e a DRE do último exercício no
                <a
                  class="briefing__link"
                  [routerLink]="[]"
                  [queryParams]="{ tab: 'documents' }"
                  queryParamsHandling="merge"
                  >Data Room</a
                >.
              </p>
            } @else {
              <p class="briefing__muted">
                O briefing é um PDF de até 3 páginas com a recomendação, os números auditados e as
                red flags, escrito pelo Cog IA a partir da base da empresa. Escolha as seções ao
                lado e gere.
              </p>
            }
          </section>
        }
      </div>
    </div>
  </div>

  <!-- O painel do chat fica fora dos cartões: transform no container pai vira stacking context e prende o painel. -->
  @if (chatOpen()) {
    <app-ai-chat-sidebar-wrapper
      [isOpen]="chatOpen()"
      [mode]="chatMode()"
      [dockedWidth]="chatDockedWidth()"
      [companyId]="companyId()"
      [defaultTab]="null"
      [title]="'Cog IA · Briefing'"
      [streamEndpointPath]="'cog/partner-deal-briefing/chat/stream'"
      [streamApiVersion]="'v2'"
      [assistantLabel]="'Cog IA'"
      [activePageLabel]="'Comercial - Briefing do deal'"
      [requireCompanySelection]="false"
      [enableFileAttachments]="false"
      [screenContextProvider]="chatContextProvider"
      [productMode]="chatProductMode()"
      (close)="closeChat()"></app-ai-chat-sidebar-wrapper>
  }
}
